import { EMR_PAGE_SOURCE_LABELS, type EmrPageSource } from "@emr/contracts";
import {
  type ClipboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { foundChart, readCapture } from "./captureIntake";
import type { ExtractedChartPage } from "./extraction";
import {
  captureFromPaste,
  type EmrPageCapture,
  type RelayChannelFactory,
  waitForRelayedCapture,
} from "./handoff";
import { bookmarkletHref } from "./pageCapture";
import "./emr-import.css";

export interface ReadChartPage {
  readonly page: ExtractedChartPage;
  readonly capturedAt: string;
}

/** What else to open in the other EMR, before sending, for the details its chart does not show. */
const MORE_DETAILS: Readonly<Record<EmrPageSource, ReactNode>> = {
  "telus-chr": (
    <>
      To bring the address and emergency contact too, open <strong>Update patient</strong> (the
      pencil beside the name); for the health card's expiry, also click <strong>Edit</strong> on the
      card there.
    </>
  ),
};

/**
 * The import box while it waits for a chart: how to send one from the other
 * EMR, the bookmark to install the first time, and a paste box for when the
 * bookmark cannot reach this tab. A chart arrives here from the tab the
 * bookmark opened (relayed in memory), from a paste, or, when this box opens
 * in that very tab, as `initialCapture`. It is read at once and only what it
 * found is passed on; the page itself is not kept.
 */
export function EmrImportPanel({
  source,
  initialCapture,
  onRead,
  onCancel,
  read = readCapture,
  relayChannel,
}: {
  readonly source: EmrPageSource;
  readonly initialCapture?: EmrPageCapture | undefined;
  readonly onRead: (chart: ReadChartPage) => void;
  readonly onCancel: () => void;
  readonly read?: (capture: EmrPageCapture) => ExtractedChartPage;
  readonly relayChannel?: RelayChannelFactory | undefined;
}) {
  const label = EMR_PAGE_SOURCE_LABELS[source];
  const titleId = useId();
  const pasteId = useId();
  const [problem, setProblem] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const bookmark = useRef<HTMLAnchorElement>(null);
  const href = useMemo(() => bookmarkletHref(window.location.origin, source), [source]);
  const accept = useRef<(capture: EmrPageCapture) => void>(() => {});
  accept.current = (capture: EmrPageCapture) => {
    if (capture.source !== source) {
      setProblem(`That chart came from another EMR, not ${label}.`);
      return;
    }
    let page: ExtractedChartPage;
    try {
      page = read(capture);
    } catch {
      setProblem(
        `BobDoc could not read that page. Open the patient's chart in ${label} and send it again.`,
      );
      return;
    }
    if (!foundChart(page)) {
      setProblem(
        `BobDoc found no patient chart on that page. Open the patient's chart in ${label} and send it again.`,
      );
      return;
    }
    setProblem(null);
    onRead({ page, capturedAt: capture.capturedAt });
  };

  // React refuses javascript: URLs in props, so the bookmark's address is set on the
  // element. Clicking it here would run the bookmark on BobDoc's own page: it is for dragging.
  useLayoutEffect(() => {
    const link = bookmark.current;
    if (link === null) return;
    link.setAttribute("href", href);
    const refuseClick = (event: MouseEvent) => {
      event.preventDefault();
      setHint("Drag the button to your bookmarks bar rather than clicking it here.");
    };
    link.addEventListener("click", refuseClick);
    return () => link.removeEventListener("click", refuseClick);
  }, [href]);

  useEffect(() => {
    if (initialCapture !== undefined) accept.current(initialCapture);
  }, [initialCapture]);

  useEffect(
    () => waitForRelayedCapture((capture) => accept.current(capture), { channel: relayChannel }),
    [relayChannel],
  );

  const paste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const pasted = captureFromPaste(event.clipboardData, source);
    if ("error" in pasted) {
      setProblem(
        pasted.error === "empty"
          ? "Nothing was pasted."
          : `That is not a chart copied from ${label}. Use Send to BobDoc, or Copy chart for BobDoc when it offers one.`,
      );
      return;
    }
    // Best effort: the chart should not stay on the clipboard once it is here.
    void navigator.clipboard?.writeText("").catch(() => {});
    accept.current(pasted.capture);
  };

  return (
    <section className="pp-import" aria-labelledby={titleId}>
      <div className="pp-import-head">
        <p className="pp-subhead" id={titleId}>
          Import from {label}
        </p>
        <button type="button" className="pp-btn ghost sm" onClick={onCancel}>
          Fill in by hand
        </button>
      </div>
      <ol className="pp-import-steps">
        <li>Open the patient's chart in {label}.</li>
        <li>{MORE_DETAILS[source]}</li>
        <li>
          Click <strong>Send to BobDoc</strong> in your bookmarks bar. The chart comes here for you
          to check; nothing is saved until you register.
        </li>
      </ol>
      <p className="pp-import-waiting" role="status">
        Waiting for {label}…
      </p>
      <div className="pp-import-setup">
        <span className="pp-quiet">First time? Drag this button to your bookmarks bar:</span>
        {/* biome-ignore lint/a11y/useValidAnchor: the href is the bookmark's javascript: address, set on the element because React refuses it; the link is dragged, not followed. */}
        <a ref={bookmark} className="pp-import-bookmark" draggable>
          Send to BobDoc
        </a>
        <button
          type="button"
          className="pp-link"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(href)
              .then(() => setHint("Copied. Add a bookmark and paste this as its address (URL)."))
              .catch(() => setHint("Copying was blocked. Drag the button instead."));
          }}
        >
          Copy the bookmark instead
        </button>
      </div>
      {hint ? (
        <p className="pp-quiet" role="status">
          {hint}
        </p>
      ) : null}
      <label className="pp-field pp-field-wide pp-import-paste" htmlFor={pasteId}>
        <span>Or paste a chart copied from {label}</span>
        <textarea
          id={pasteId}
          rows={2}
          value=""
          placeholder="Click here, then press Ctrl+V"
          onPaste={paste}
          onChange={() => {}}
        />
      </label>
      {problem ? (
        <p className="pp-field-error" role="alert">
          {problem}
        </p>
      ) : null}
    </section>
  );
}
